import { useCallback, useMemo } from 'react'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter, { type ModalFooterActionProps } from 'LuxKit/Modal/ModalFooter'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { VoucherDisplayConfigVoucher } from 'api/promo'
import { getVoucherCallToAction, isAbsoluteVoucherCallToActionUrl } from 'lib/voucher/getVoucherCallToAction'
import VoucherDetailsContent from './VoucherDetailsContent'

type VoucherConditionResult = NonNullable<App.PublicVoucher['conditions']>[number]['rewardGroup']['results'][number]

type VoucherAnyType = App.PublicVoucher | App.PublicVoucherCalculated | VoucherDisplayConfigVoucher

type Props = {
  title: string
  voucher: VoucherAnyType
  conditionResults?: Array<VoucherConditionResult>
}

function VoucherDetailsModal({ title, voucher, conditionResults }: Props) {
  const modalElement = useModalElementContext()

  const onClose = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  const primaryActionProps = useMemo((): ModalFooterActionProps | undefined => {
    const callToAction = getVoucherCallToAction(voucher)
    if (!callToAction) return undefined
    if (isAbsoluteVoucherCallToActionUrl(callToAction.url)) {
      return {
        children: callToAction.text,
        href: callToAction.url,
        target: '_blank',
        rel: 'noopener noreferrer',
      }
    }
    return {
      children: callToAction.text,
      to: callToAction.url,
    }
  }, [voucher])

  return <ModalBase size="L">
    <ModalHeader
      title={title}
      onCloseButtonClick={onClose}
    />
    <ModalBody>
      <ModalContent>
        <VoucherDetailsContent voucher={voucher} conditionResults={conditionResults} />
      </ModalContent>
    </ModalBody>
    {primaryActionProps && <ModalFooter primaryActionProps={primaryActionProps} />}
  </ModalBase>
}

export default VoucherDetailsModal
